import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import CrisisButton from "@/components/CrisisButton";
import { Logo } from "@/components/Logo";
import { BreakChainProvider, BreakChainButton } from "@/components/BreakChain";
import {
  Sunrise, Compass, CalendarClock, TrendingUp, MessageCircleHeart, LogOut, Menu, X, Heart, Sparkles, BadgeCheck, HandHeart,
} from "lucide-react";

const NAV = [
  { to: "/app", label: "Today", icon: Sunrise, end: true },
  { to: "/app/journey", label: "My Journey", icon: Compass },
  { to: "/app/plan", label: "My Plan", icon: CalendarClock },
  { to: "/app/tracker", label: "Progress", icon: TrendingUp },
  { to: "/app/coach", label: "Companion", icon: MessageCircleHeart },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [imgError, setImgError] = useState(false);

  const isMember = !!user?.membership?.is_member;
  const goMission = () => { setOpen(false); navigate("/app/mission"); };
  const goMembership = () => { setOpen(false); navigate("/app/membership"); };
  const goTogether = () => { setOpen(false); navigate("/app/together"); };

  const NavItems = ({ onClick }) => (
    <>
      {NAV.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onClick}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s|\d+/g, "")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-medium tracking-wide transition-colors duration-200 ${
                isActive ? "bg-olive text-sand" : "text-olive/70 hover:bg-sage"
              }`
            }
          >
            <Icon className="w-[18px] h-[18px]" />
            {item.label}
          </NavLink>
        );
      })}
    </>
  );

  return (
    <BreakChainProvider>
      <div className="min-h-screen bg-sand">
        {/* Mobile top bar */}
        <div className="lg:hidden sticky top-0 z-40 flex items-center justify-between gap-2 bg-sand/95 backdrop-blur border-b border-border px-4 py-2.5">
          <button onClick={() => navigate("/app")} className="flex items-center gap-2 min-w-0" data-testid="brand-mobile">
            <Logo className="h-7 w-10 shrink-0" />
            <span className="font-heading font-extrabold tracking-[0.1em] text-brand-navy text-sm truncate">NOT BY WILL</span>
          </button>
          <div className="flex items-center gap-1.5 shrink-0">
            <BreakChainButton variant="mobile" />
            <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="p-2 text-olive shrink-0">
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden fixed inset-0 z-30 bg-sand pt-16 px-5 overflow-y-auto" data-testid="mobile-menu">
            <nav className="flex flex-col gap-1.5 mt-4">
              <NavItems onClick={() => setOpen(false)} />
            </nav>
            <div className="mt-4">
              <BreakChainButton variant="sidebar" className="w-full" />
            </div>
            <button onClick={goMembership} data-testid="continue-journey-mobile" className="mt-3 w-full flex items-center justify-center gap-2 rounded-2xl bg-brand-navy text-brand-ivory px-4 py-3 text-sm font-medium hover:bg-brand-navy/90 transition-colors">
              {isMember ? <><BadgeCheck className="w-4 h-4 text-brand-teal" /> Not By Will Member</> : <><Sparkles className="w-4 h-4 text-brand-teal" /> Continue your journey</>}
            </button>
            <button onClick={goTogether} data-testid="prayer-wall-mobile" className="mt-2 w-full flex items-center justify-center gap-2 rounded-2xl border border-brand-teal/30 text-brand-teal-dark px-4 py-3 text-sm font-medium hover:bg-sage transition-colors">
              <HandHeart className="w-4 h-4" /> Prayer Wall
            </button>
            <button onClick={goMission} data-testid="support-mobile" className="mt-2 w-full flex items-center justify-center gap-2 rounded-2xl border border-brand-teal/30 text-brand-teal-dark px-4 py-3 text-sm font-medium hover:bg-sage transition-colors">
              <Heart className="w-4 h-4" /> Support this ministry
            </button>
            <button onClick={logout} data-testid="logout-mobile" className="mt-4 flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-medium text-olive/70 hover:bg-sage transition-colors">
              <LogOut className="w-[18px] h-[18px]" /> Sign out
            </button>
          </div>
        )}

        <div className="flex">
          {/* Sidebar */}
          <aside className="hidden lg:flex flex-col w-72 shrink-0 h-screen sticky top-0 border-r border-border bg-sand p-6">
            <button onClick={() => navigate("/app")} className="flex items-center gap-2.5 mb-9 px-2" data-testid="brand-desktop">
              <Logo className="h-9 w-14 shrink-0" />
              <div className="text-left leading-none">
                <p className="font-heading font-extrabold tracking-[0.12em] text-brand-navy text-lg">NOT BY WILL</p>
                <p className="text-[10px] tracking-[0.1em] uppercase text-brand-teal-dark mt-1.5">By His strength. And our faith.</p>
              </div>
            </button>

            <nav className="flex flex-col gap-1.5 flex-1">
              <NavItems />
              <div className="mt-5 pt-5 border-t border-border">
                <BreakChainButton variant="sidebar" className="w-full" />
                <p className="text-[11px] text-stone text-center mt-2.5 leading-snug px-1">
                  Feeling an urge right now? Pause here first.
                </p>
              </div>
            </nav>

            <div className="mt-6 border-t border-border pt-5">
              <div className="flex items-center gap-3 px-2 mb-3">
                {user?.picture && !imgError ? (
                  <img src={user.picture} alt="" onError={() => setImgError(true)} className="w-9 h-9 rounded-full object-cover" referrerPolicy="no-referrer" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-sage flex items-center justify-center font-serif text-olive">
                    {user?.name?.[0] || "F"}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-medium text-olive truncate" data-testid="user-name">{user?.name || "Friend"}</p>
                    {isMember && (
                      <span data-testid="member-badge" className="inline-flex items-center gap-1 rounded-full bg-brand-teal/15 text-brand-teal-dark text-[10px] font-semibold px-2 py-0.5 shrink-0">
                        <BadgeCheck className="w-3 h-3" /> Member
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-stone truncate">{user?.email}</p>
                </div>
              </div>
              <button onClick={goMembership} data-testid="continue-journey-desktop" className="mb-1 flex w-full items-center gap-3 rounded-2xl px-4 py-2.5 text-sm font-medium text-brand-navy bg-sage/70 hover:bg-sage transition-colors">
                {isMember ? <><BadgeCheck className="w-[18px] h-[18px] text-brand-teal-dark" /> Not By Will Member</> : <><Sparkles className="w-[18px] h-[18px] text-brand-teal-dark" /> Continue your journey</>}
              </button>
              <button onClick={goTogether} data-testid="prayer-wall-desktop" className="mb-1 flex w-full items-center gap-3 rounded-2xl px-4 py-2.5 text-sm text-brand-teal-dark hover:bg-sage transition-colors">
                <HandHeart className="w-[18px] h-[18px]" /> Prayer Wall
              </button>
              <button onClick={logout} data-testid="logout-desktop" className="flex w-full items-center gap-3 rounded-2xl px-4 py-2.5 text-sm text-olive/70 hover:bg-sage transition-colors">
                <LogOut className="w-[18px] h-[18px]" /> Sign out
              </button>
              <button onClick={goMission} data-testid="support-desktop" className="mt-1 flex w-full items-center gap-3 rounded-2xl px-4 py-2.5 text-sm text-brand-teal-dark hover:bg-sage transition-colors">
                <Heart className="w-[18px] h-[18px]" /> Support this ministry
              </button>
            </div>
          </aside>

          {/* Content */}
          <main className="flex-1 min-w-0 px-5 sm:px-8 py-8 pb-28 max-w-6xl mx-auto w-full">
            <Outlet />
          </main>
        </div>

        <CrisisButton />
      </div>
    </BreakChainProvider>
  );
}
